import { NotificationManager } from '../../components/notification-manager';
import { ReusableCoupon, ReusableCouponInput } from '../../stores/loyalty-club';
import {
    buildReusableCouponPayload,
    nextReusableCouponRowUid,
    ReusableCouponPromotion,
    ReusableCouponRow,
    validateReusableCoupons,
} from './components/UnlimitedCoupons';

export const REPEATABLE_PURCHASE_GRANT = 'repeatablePurchaseGrant';

/** Mobile renders the contest card only for repeatable-grant clubs. */
export function canLinkRelatedGame(clubType: string | null): boolean {
    return clubType === REPEATABLE_PURCHASE_GRANT;
}

export function relatedGameIdForSave(
    clubType: string | null,
    externalGameId: number | null
): number | null {
    return canLinkRelatedGame(clubType) ? externalGameId : null;
}

/**
 * Rebuilds the editor rows from what the replace endpoint saved. Names and row
 * keys come from the rows that were sent, since the response carries neither,
 * and a row given a new key would lose focus.
 */
export function toReusableCouponRows(
    saved: ReusableCoupon[],
    sent: ReusableCouponRow[]
): ReusableCouponRow[] {
    return [...saved]
        .sort((a, b) => a.position - b.position)
        .map((coupon) => {
            const sentRow = sent.find(
                (row) => row.promotionId === coupon.promotionId
            );
            return {
                uid: sentRow?.uid ?? nextReusableCouponRowUid(),
                id: coupon.id,
                promotionId: coupon.promotionId,
                startDate: coupon.startDate,
                endDate: coupon.endDate,
                promotionName: sentRow?.promotionName ?? null,
                thumbnail: sentRow?.thumbnail ?? null,
            };
        });
}

export type ClubSaveHandlers = {
    saveClub: () => Promise<void>;
    saveReusableCoupons: (
        coupons: ReusableCouponInput[]
    ) => Promise<ReusableCoupon[]>;
    /** Commits the club's baselines once its PUT landed. */
    onClubSaved: () => void;
    /** Takes the rows the coupon write returned and their payload JSON. */
    onReusableCouponsSaved: (
        rows: ReusableCouponRow[],
        savedPayloadJson: string
    ) => void;
};

/**
 * Saves a club and its reusable coupons, in that order.
 *
 * These are two endpoints with no transaction spanning them, so the club can
 * save while the coupons fail. That half-landed case gets its own message,
 * because the generic network error leaves the operator unable to tell what
 * saved. The rows and the dirty flag are left alone, so saving again retries
 * the coupons. The club PUT is re-sent on that retry too: it is idempotent
 * (same values, and the schedule baseline was already committed) so skipping
 * it would add a second dirty-tracking path for no observable difference.
 *
 * `promotions` is the picker's coupon list, which backs the privacy rule.
 * `null` means the list has not loaded, which is different from an empty
 * list: with rows to check and nothing to check them against, the save is
 * refused rather than let the club write land and the coupon write fail.
 */
export async function saveClubWithReusableCoupons(
    couponRows: ReusableCouponRow[],
    savedCouponPayloadJson: string,
    handlers: ClubSaveHandlers,
    promotions: ReusableCouponPromotion[] | null = []
): Promise<void> {
    if (promotions === null && couponRows.length > 0) {
        NotificationManager.showError(
            'Coupon details are still loading. Try saving again in a moment.'
        );
        throw new Error('Reusable coupon promotions not loaded');
    }

    // Also covers the save the unsaved-changes modal triggers, which bypasses
    // the Save button and its disabled state.
    if (
        Object.keys(validateReusableCoupons(couponRows, promotions ?? []))
            .length > 0
    ) {
        NotificationManager.showError(
            'Fix the highlighted unlimited coupons before saving.'
        );
        throw new Error('Invalid reusable coupons');
    }

    await handlers.saveClub();
    handlers.onClubSaved();

    // Guarded because the endpoint replaces every row and reassigns positions.
    const couponPayload = buildReusableCouponPayload(couponRows);
    if (JSON.stringify(couponPayload) !== savedCouponPayloadJson) {
        let saved: ReusableCoupon[];
        try {
            saved = await handlers.saveReusableCoupons(couponPayload);
        } catch (error) {
            NotificationManager.showError(
                'The club details saved, but the unlimited coupons did not. Your coupon rows are still here, so saving again retries just them.',
                10
            );
            throw error;
        }

        const savedRows = toReusableCouponRows(saved, couponRows);
        handlers.onReusableCouponsSaved(
            savedRows,
            JSON.stringify(buildReusableCouponPayload(savedRows))
        );
    }

    NotificationManager.showSuccess('Loyalty club updated');
}
